import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import {
  ArrowCircleDown,
  Visibility,
  DriveFileRenameOutline,
} from '@mui/icons-material';
import {
  type InterviewListPageData,
  useGetInterviewListPageData,
} from './graphql';
import { INTERVIEW_LIST_PAGE_TEXT } from './components';
import { INTERVIEW_PANEL_BUTTON } from './const';
import {
  APPLICATION_STATUS_TYPE,
  fundingCell,
  APPLICATION_STATUS_EXPLANATION_MAP,
  ASSESSMENT_STATUS_DISPLAY_MAP,
} from '../../utils';
import {
  type HeadCell,
  Table,
  Alert,
  type AlertDetails,
  IconButton,
  Loading,
  Button,
  DeepMerge,
} from '../../components';
import {
  DSRC_FILTER_API_TYPE,
  DSRCFilter,
  OneOffDialog,
  type OneOffDialogConfig,
  OverViewRole,
} from '../Assessment';

// InterviewList uses buttons to set the initial filter of <InterviewListPage />
// set key to <InterviewListPage /> to force it re-render when activated button changes, clear the content in table filter as well

// This component uses buttons to control two lists
// 1. TO-DO list
// 2. All list

export const InterviewList: React.FunctionComponent = () => {
  const [activateButton, setActivateButton] = useState<INTERVIEW_PANEL_BUTTON>(
    INTERVIEW_PANEL_BUTTON.TO_DO,
  );

  const handleChangeFilterButton = (button: INTERVIEW_PANEL_BUTTON) => () => {
    setActivateButton(button);
  };

  const buttonArea: React.ReactNode = (
    <div className="flex flex-row">
      {[INTERVIEW_PANEL_BUTTON.TO_DO, INTERVIEW_PANEL_BUTTON.ALL]?.map(
        (item, index) => {
          return (
            <div key={index} className="mr-4">
              <Button
                sx={{ textTransform: 'none' }}
                onClick={handleChangeFilterButton(item)}
                variant={activateButton === item ? 'contained' : 'outlined'}
              >
                {item}
              </Button>
            </div>
          );
        },
      )}
    </div>
  );
  const interviewListMap: Record<INTERVIEW_PANEL_BUTTON, React.ReactNode> = {
    [INTERVIEW_PANEL_BUTTON.TO_DO]: (
      <InterviewListPage activateButton={INTERVIEW_PANEL_BUTTON.TO_DO} key={0}>
        {buttonArea}
      </InterviewListPage>
    ),
    [INTERVIEW_PANEL_BUTTON.ALL]: (
      <InterviewListPage activateButton={INTERVIEW_PANEL_BUTTON.ALL} key={1}>
        {buttonArea}
      </InterviewListPage>
    ),
  };

  return interviewListMap[activateButton];
};

// <InterviewListPage /> includes table filter + table
// initial filters and filters from table filter can be combined to filter the list

type InterviewListProps = {
  activateButton: INTERVIEW_PANEL_BUTTON;
  children?: React.ReactNode;
};

const filterMap: Record<INTERVIEW_PANEL_BUTTON, any> = {
  [INTERVIEW_PANEL_BUTTON.TO_DO]: {
    status: { eq: APPLICATION_STATUS_TYPE.UNDER_INTERVIEW_PANEL },
  },
  [INTERVIEW_PANEL_BUTTON.ALL]: {},
};

export const InterviewListPage: React.FC<InterviewListProps> = props => {
  const { activateButton, children } = props;
  const navigate = useNavigate();
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [oneOffDialogConfig, setOpenOneOffDialog] =
    useState<OneOffDialogConfig>({
      open: false,
      applicationId: '',
      applicationNo: '',
    });
  const [appliedFilter, setAppliedFilter] = useState<any>(null);

  const [filter, setFilter] = useState({
    ...filterMap[activateButton],
  });

  const { data, loading, error } = useGetInterviewListPageData(filter);

  const headerCellsData: Array<HeadCell<InterviewListPageData>> = [
    {
      id: 'applicationNo',
      label: INTERVIEW_LIST_PAGE_TEXT.APPLICATION_NO,
      isSortable: false,
      render: (row: InterviewListPageData) => row.applicationNo,
    },
    {
      id: 'applicantName',
      label: INTERVIEW_LIST_PAGE_TEXT.NAME,
      isSortable: false,
      render: (row: InterviewListPageData) => row.applicantName,
    },
    {
      id: 'entryYear',
      label: INTERVIEW_LIST_PAGE_TEXT.ENTRY_YEAR,
      isSortable: false,
      render: (row: InterviewListPageData) => row.entryYear,
    },

    {
      id: 'department',
      label: INTERVIEW_LIST_PAGE_TEXT.DEPARTMENT,
      isSortable: false,
      render: (row: InterviewListPageData) => row?.department,
    },
    {
      id: 'programme',
      label: INTERVIEW_LIST_PAGE_TEXT.PROGRAMME,
      isSortable: false,
      render: (row: InterviewListPageData) => row.programme,
    },
    {
      id: 'studyMode',
      label: INTERVIEW_LIST_PAGE_TEXT.STUDY_MODE,
      isSortable: false,
      render: (row: InterviewListPageData) => row.studyMode,
    },
    {
      id: 'researchArea',
      label: INTERVIEW_LIST_PAGE_TEXT.RESEARCH_AREA,
      isSortable: false,
      render: (row: InterviewListPageData) => row.researchArea,
    },
    {
      id: 'chiefSupervisor',
      label: INTERVIEW_LIST_PAGE_TEXT.CHIEF_SUPERVISOR,
      isSortable: false,
      render: (row: InterviewListPageData) => row.chiefSupervisor,
    },

    {
      id: 'fundingScheme',
      label: INTERVIEW_LIST_PAGE_TEXT.FUNDING_SCHEME,
      isSortable: false,
      render: (row: InterviewListPageData) =>
        fundingCell(row.fundingScheme ?? []),
    },
    {
      id: 'status',
      label: INTERVIEW_LIST_PAGE_TEXT.STATUS,
      isSortable: false,
      render: (row: InterviewListPageData) => {
        const rowStatus = row.status as keyof typeof APPLICATION_STATUS_TYPE;
        const explanation = APPLICATION_STATUS_EXPLANATION_MAP[rowStatus];
        return (
          <Tooltip title={explanation}>
            <span>{ASSESSMENT_STATUS_DISPLAY_MAP[rowStatus]}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'action',
      label: INTERVIEW_LIST_PAGE_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: InterviewListPageData) => action(row),
    },
  ];

  const action = (row: InterviewListPageData): React.ReactNode => {
    const { applicationId, applicationNo } = row;

    return (
      <div className="flex justify-center items-center w-full">
        <div className="grid grid-cols-2">
          {activateButton === INTERVIEW_PANEL_BUTTON.TO_DO && (
            <IconButton
              onClick={handleAccessInterviewReport(applicationId)}
              color={'primary'}
              icon={<DriveFileRenameOutline />}
            />
          )}
          <IconButton
            onClick={handleViewMonitor(applicationId)}
            title={INTERVIEW_LIST_PAGE_TEXT.VIEW_APPLICATION_DETAIL}
            color="primary"
            icon={<Visibility />}
          />
          <IconButton
            onClick={() => {
              setOpenOneOffDialog({
                open: true,
                applicationId,
                applicationNo,
              });
            }}
            title={INTERVIEW_LIST_PAGE_TEXT.DOWNLOAD_APPLICATION_PROFILE}
            color="primary"
            icon={<ArrowCircleDown />}
          />
        </div>
      </div>
    );
  };

  const handleAccessInterviewReport =
    (applicationId: string): (() => void) =>
    () => {
      navigate(`/internal/interview-detail/${applicationId}`);
    };
  const handleViewMonitor =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) {
        window.open(`/internal/monitor/${applicationId}`);
      } else {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: INTERVIEW_LIST_PAGE_TEXT.INVALID_APPLICATIONID,
          alertType: 'warning',
        });
      }
    };

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }
  const { open: openDialog } = oneOffDialogConfig;

  return (
    <div className="w-full">
      {openDialog && (
        <OneOffDialog
          openDialogConfig={oneOffDialogConfig}
          onDialogClose={() => {
            setOpenOneOffDialog({
              open: false,
              applicationId: '',
              applicationNo: '',
            });
          }}
        />
      )}

      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div className="w-full pr-4">
        <DSRCFilter
          overViewRole={OverViewRole.INTERVIEW_PANEL}
          initialAppliedFilter={appliedFilter}
          setFilter={(filter: any, appliedFilter: any) => {
            setFilter(
              DeepMerge(filter, {
                ...filterMap[activateButton],
              }),
            );
            setAppliedFilter(appliedFilter);
          }}
          apiType={DSRC_FILTER_API_TYPE.interviewPanelApplicationList}
        />
      </div>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="w-full py-2">{children}</div>
          <div className="pr-4">
            <Table<InterviewListPageData>
              headCells={headerCellsData}
              data={data}
              showIndex
            />
          </div>
        </>
      )}
    </div>
  );
};
